import React, { useRef, useState } from "react";
import { X, Download, Eye, FileText, Printer, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import PrescriptionPadView from "./PrescriptionPadView";
import { IDoctorPrescriptionData } from "../../../types/prescription";

interface PrescriptionPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  prescriptionData: Partial<IDoctorPrescriptionData>;
  defaultDoctorData?: {
    name?: string;
    education?: string | string[];
    specialization?: string[];
    registrationNumber?: string;
    hospitalName?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
  };
}

export const PrescriptionPreviewModal: React.FC<PrescriptionPreviewModalProps> = ({
  isOpen,
  onClose,
  prescriptionData,
  defaultDoctorData,
}) => {
  const [previewMode, setPreviewMode] = useState<"template" | "sample">("template");
  const [isDownloading, setIsDownloading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const doctorName =
    prescriptionData.doctorName || defaultDoctorData?.name || "Doctor";

  // Trigger Browser Print Preview with standard A4 page setup and proper margins
  const handlePrint = () => {
    const element = document.getElementById("prescription-pad-modal-content");
    if (!element) {
      toast.error("Prescription content not found");
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) return;

    // Collect all stylesheets from head
    const styleTags = Array.from(
      document.querySelectorAll("link[rel='stylesheet'], style")
    )
      .map((el) => el.outerHTML)
      .join("\n");

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Prescription_${doctorName.replace(/\s+/g, "_")}</title>
          ${styleTags}
          <style>
            @page {
              size: A4 portrait;
              margin: 12mm 14mm 12mm 14mm;
            }
            * {
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              width: 100% !important;
              height: 100% !important;
            }
            #prescription-pad-modal-print {
              border: none !important;
              box-shadow: none !important;
              padding: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              height: 100% !important;
              min-height: calc(297mm - 28mm) !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
            }
          </style>
        </head>
        <body>
          ${element.innerHTML}
        </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 2500);
    }, 400);
  };

  // Direct PDF Download
  const handleDownloadPdf = async () => {
    try {
      setIsDownloading(true);
      toast.loading("Generating PDF...", { id: "pdf-download" });

      const element = document.getElementById("prescription-pad-modal-content");
      if (!element) {
        throw new Error("Prescription preview element not found");
      }

      await document.fonts.ready;

      const canvas = await html2canvas(element, {
        scale: 2.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#FFFFFF",
        windowWidth: 1000,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 5;
      const availableWidth = pdfWidth - 2 * margin;
      const availableHeight = pdfHeight - 2 * margin;

      const imgHeight = (canvas.height * availableWidth) / canvas.width;

      if (imgHeight <= availableHeight) {
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          margin,
          availableWidth,
          imgHeight,
          undefined,
          "FAST"
        );
      } else {
        const scaledWidth = (canvas.width * availableHeight) / canvas.height;
        const xOffset = (pdfWidth - scaledWidth) / 2;
        pdf.addImage(
          imgData,
          "PNG",
          xOffset,
          margin,
          scaledWidth,
          availableHeight,
          undefined,
          "FAST"
        );
      }

      const safeName = doctorName.replace(/[^a-zA-Z0-9_-]/g, "_");
      pdf.save(`Prescription_Template_${safeName}.pdf`);
      toast.success("Prescription PDF downloaded successfully!", {
        id: "pdf-download",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.", {
        id: "pdf-download",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#771D51]/10 flex items-center justify-center text-[#771D51]">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-none">
                Prescription Template Preview
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Print or download doctor's custom prescription pad
              </p>
            </div>
          </div>

          {/* Action Bar */}
          <div className="flex items-center gap-2.5">
            {/* Template vs Sample toggle */}
            <div className="hidden sm:flex bg-gray-100 p-1 rounded-lg border border-gray-200 text-xs font-medium">
              <button
                type="button"
                onClick={() => setPreviewMode("template")}
                className={`px-3 py-1.5 rounded-md transition-all ${previewMode === "template"
                    ? "bg-white text-[#771D51] font-semibold shadow-xs"
                    : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                Blank Template
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode("sample")}
                className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-1.5 ${previewMode === "sample"
                    ? "bg-white text-[#771D51] font-semibold shadow-xs"
                    : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                <Eye className="w-3.5 h-3.5" />
                Sample Preview
              </button>
            </div>

            {/* Print Preview Button */}
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-1.5 bg-[#771D51] hover:bg-[#5E1640] text-white px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all shadow-sm cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              <span>Print / Preview</span>
            </button>

            {/* PDF Download Button */}

            {/* Close Modal */}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-4 sm:p-8 overflow-y-auto bg-gray-100/70 flex justify-center">
          <div
            ref={printRef}
            id="prescription-pad-modal-content"
            className="w-full max-w-[820px] bg-white rounded-lg shadow-md"
          >
            <PrescriptionPadView
              prescriptionData={prescriptionData}
              defaultDoctorData={defaultDoctorData}
              mode={previewMode}
              containerId="prescription-pad-modal-print"
            />
          </div>
        </div>

        {/* Modal Footer Note */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>
            {previewMode === "template"
              ? "Showing printable blank prescription template pad with empty patient fields."
              : "Showing prescription template populated with sample consultation and vital stats."}
          </span>
          <div className="sm:hidden flex gap-2">
            <button
              onClick={() =>
                setPreviewMode(previewMode === "template" ? "sample" : "template")
              }
              className="text-[#771D51] font-semibold underline"
            >
              Toggle {previewMode === "template" ? "Sample" : "Template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionPreviewModal;
